import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Badge } from '@atoms/Badge';
import { Button } from '@atoms/Button';
import { Card } from '@atoms/Card';
import { Modal } from '@atoms/Modal';
import { Spinner } from '@atoms/Spinner';
import { subscriptionService } from '@services/subscriptionService';
import { getErrorMessage } from '@services/api';
import type { SubscriptionPlanInfo } from '@/types';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount);
}

export function SubscriptionSection() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null,
  );
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Surface the Iyzico payment result coming back via redirect.
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment) return;
    if (payment === 'success') {
      setFeedback({ kind: 'success', text: 'Ödemeniz alındı, aboneliğiniz aktifleştirildi.' });
    } else {
      const reason = searchParams.get('reason');
      setFeedback({
        kind: 'error',
        text: reason || 'Ödeme tamamlanamadı. Lütfen tekrar deneyin.',
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    next.delete('reason');
    setSearchParams(next, { replace: true });
    void queryClient.invalidateQueries({ queryKey: ['restaurant', 'subscription'] });
  }, [searchParams, setSearchParams, queryClient]);

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['restaurant', 'subscription', 'me'],
    queryFn: () => subscriptionService.getMySubscription(),
  });

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['restaurant', 'subscription', 'plans'],
    queryFn: () => subscriptionService.getPlans(),
  });

  const checkoutMutation = useMutation({
    mutationFn: (planId: string) => subscriptionService.startCheckout(planId),
    onSuccess: (session) => {
      if (session.paymentPageUrl) {
        window.location.href = session.paymentPageUrl;
        return;
      }
      if (session.checkoutFormContent) {
        // Hosted form fallback: render Iyzico's embedded checkout snippet.
        const container = document.getElementById('iyzico-checkout-container');
        if (container) {
          container.innerHTML = '<div id="iyzipay-checkout-form" class="responsive"></div>';
          const range = document.createRange();
          range.selectNode(container);
          container.appendChild(range.createContextualFragment(session.checkoutFormContent));
          container.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      setFeedback({ kind: 'error', text: 'Ödeme sayfası açılamadı. Lütfen tekrar deneyin.' });
    },
    onError: (err) => setFeedback({ kind: 'error', text: getErrorMessage(err) }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => subscriptionService.cancel(),
    onSuccess: (res) => {
      setCancelModalOpen(false);
      setFeedback({
        kind: 'success',
        text: `Aboneliğiniz iptal edildi. ${
          res.accessUntil ? `Erişiminiz ${formatDate(res.accessUntil)} tarihine kadar devam eder.` : ''
        }`,
      });
      void queryClient.invalidateQueries({ queryKey: ['restaurant', 'subscription'] });
    },
    onError: (err) => setFeedback({ kind: 'error', text: getErrorMessage(err) }),
  });

  const retryMutation = useMutation({
    mutationFn: () => subscriptionService.retryPayment(),
    onSuccess: () => {
      setFeedback({ kind: 'success', text: 'Ödeme tekrar denendi. Sonuç kısa süre içinde yansıyacaktır.' });
      void queryClient.invalidateQueries({ queryKey: ['restaurant', 'subscription'] });
    },
    onError: (err) => setFeedback({ kind: 'error', text: getErrorMessage(err) }),
  });

  if (meLoading || plansLoading) return <Spinner />;

  const access = me?.access;
  const subscription = me?.subscription;
  const payments = me?.payments ?? [];
  const hasActiveSubscription = access?.status === 'active' && Boolean(subscription);

  return (
    <div className="space-y-6">
      {feedback && (
        <div
          className={
            feedback.kind === 'success'
              ? 'flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800'
              : 'flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800'
          }
        >
          {feedback.kind === 'success' ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <span>{feedback.text}</span>
        </div>
      )}

      {/* Current status */}
      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand-500" />
            <h2 className="text-lg font-semibold text-gray-900">Üyelik Durumu</h2>
          </div>
          {access?.status === 'trial' && <Badge variant="info">Deneme Sürümü</Badge>}
          {access?.status === 'active' && !access.cancelsAtPeriodEnd && (
            <Badge variant="success">Aktif Abonelik</Badge>
          )}
          {access?.status === 'active' && access.cancelsAtPeriodEnd && (
            <Badge variant="warning">İptal Edildi — Dönem Sonuna Kadar Aktif</Badge>
          )}
          {access?.status === 'expired' && <Badge variant="danger">Süresi Doldu</Badge>}
        </div>

        {access?.status === 'trial' && (
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 p-4">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">
                Deneme sürenizin bitmesine {access.trialDaysLeft} gün kaldı
              </p>
              <p className="mt-1">
                Deneme süreniz {formatDate(access.trialEndsAt)} tarihinde sona erecek. Kesintisiz
                kullanım için aşağıdan bir plan seçebilirsiniz.
              </p>
            </div>
          </div>
        )}

        {access?.status === 'expired' && (
          <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="text-sm text-red-900">
              <p className="font-semibold">Deneme süreniz sona erdi</p>
              <p className="mt-1">
                Toptancılara sipariş vermeye devam etmek için aylık üyelik planlarından birini
                seçmeniz gerekiyor.
              </p>
            </div>
          </div>
        )}

        {subscription && (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Plan</p>
              <p className="font-semibold text-gray-900">{subscription.plan?.name ?? '—'}</p>
              {subscription.plan && (
                <p className="text-sm text-gray-600">
                  {formatPrice(subscription.plan.price, subscription.plan.currency)}/ay
                </p>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Mevcut Dönem</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(subscription.currentPeriodStart)} —{' '}
                {formatDate(subscription.currentPeriodEnd)}
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Durum</p>
              <p className="text-sm font-medium text-gray-900">
                {subscription.status === 'active' && 'Aktif'}
                {subscription.status === 'canceled' && 'İptal edildi'}
                {subscription.status === 'unpaid' && 'Ödeme bekleniyor'}
                {subscription.status === 'pending' && 'Beklemede'}
                {subscription.status === 'expired' && 'Süresi doldu'}
                {subscription.status === 'upgraded' && 'Yükseltildi'}
              </p>
            </div>
          </div>
        )}

        {subscription?.status === 'unpaid' && (
          <Button
            variant="secondary"
            loading={retryMutation.isPending}
            onClick={() => retryMutation.mutate()}
          >
            <CreditCard className="h-4 w-4" /> Ödemeyi Tekrar Dene
          </Button>
        )}

        {hasActiveSubscription && !access?.cancelsAtPeriodEnd && (
          <Button variant="ghost" className="text-red-600" onClick={() => setCancelModalOpen(true)}>
            Aboneliği iptal et
          </Button>
        )}
      </Card>

      {/* Plans */}
      {!hasActiveSubscription && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-gray-900">Aylık Üyelik Planları</h2>
          {me && !me.paymentsConfigured && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Ödeme sistemi şu anda yapılandırılmamış. Lütfen platform yöneticisi ile iletişime
              geçin.
            </div>
          )}
          {!plans?.length ? (
            <Card className="p-6 text-sm text-gray-500">
              Henüz satışta bir plan bulunmuyor. Lütfen daha sonra tekrar kontrol edin.
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {plans.map((plan: SubscriptionPlanInfo) => (
                <Card key={plan.id} className="flex flex-col gap-3 p-5">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-5 w-5 text-brand-500" />
                    <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {formatPrice(plan.price, plan.currency)}
                    <span className="text-sm font-medium text-gray-500"> /ay</span>
                  </p>
                  {plan.description && (
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  )}
                  <div className="mt-auto pt-2">
                    <Button
                      fullWidth
                      disabled={!me?.paymentsConfigured}
                      loading={
                        checkoutMutation.isPending && checkoutMutation.variables === plan.id
                      }
                      onClick={() => checkoutMutation.mutate(plan.id)}
                    >
                      <CreditCard className="h-4 w-4" /> Abone Ol
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Ödemeleriniz iyzico güvencesiyle alınır; kart bilgileriniz platformumuzda saklanmaz.
            Abonelik her ay otomatik yenilenir, dilediğiniz zaman iptal edebilirsiniz.
          </p>
          <div id="iyzico-checkout-container" className="mt-4" />
        </div>
      )}

      {/* Payment history */}
      {payments.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="font-semibold text-gray-900">Ödeme Geçmişi</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {payments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatPrice(payment.amount, payment.currency)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(payment.paidAt ?? payment.createdAt)}
                  </p>
                </div>
                <Badge variant={payment.status === 'success' ? 'success' : 'danger'}>
                  {payment.status === 'success' ? 'Başarılı' : 'Başarısız'}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Modal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Aboneliği iptal et"
      >
        <p className="text-sm text-gray-600">
          Aboneliğinizi iptal etmek istediğinize emin misiniz? Mevcut dönem sonuna kadar
          ({formatDate(subscription?.currentPeriodEnd)}) erişiminiz devam eder; sonrasında yeni
          sipariş veremezsiniz.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
            Vazgeç
          </Button>
          <Button
            variant="danger"
            loading={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            Evet, iptal et
          </Button>
        </div>
      </Modal>
    </div>
  );
}
